<template>
  <div class="document-view"
       v-if="isDataReady">
    <div class="document-view-wrap">
      <section class="header">
        <h1>Web Accessibility Diagnostic Report</h1>
        <p>
          <span class="group">{{ reportDatum.ReportGroup.name }}</span>
          {{ reportDatum.title }}
        </p>
      </section>

      <section class="break">
        <h2>
          <i class="icon logo--seeall"></i> Diagnostic information
        </h2>
        <div class="section-content">
          <table class="report-info">
            <tr>
              <th>URL</th>
              <td><a :href="reportDatum.url" class="url" target="_blank">{{ reportDatum.url }}</a></td>
            </tr>
            <tr>
              <th>Account</th>
              <td>{{ reportDatum.ReportGroup.User.email }}</td>
            </tr>
            <tr>
              <th>Report URL</th>
              <td>{{ reportUrl }}</td>
            </tr>
            <tr>
              <th>Diagnostic time</th>
              <td>{{ report.runAt | yymmddhhmm }}</td>
            </tr>
            <tr>
              <th>Resolution</th>
              <td>{{ deviceType }}</td>
            </tr>
            <tr>
              <th>{{ $t('report.loginInfo') }}</th>
              <td v-if="reportDatum.requireLogin">
                <ul>
                  <li>URL : {{ reportDatum.loginUrl }}</li>
                  <li>ID : {{ reportDatum.loginId }} / {{ reportDatum.loginIdSelector }}</li>
                  <li>PW : ******** / {{ reportDatum.loginPasswordSelector }}</li>
                  <li>Submit : {{ reportDatum.loginButtonSelector }}</li>
                </ul>
              </td>
              <td v-else>{{ $t('report.notAvailable') }}</td>
            </tr>
            <tr>
              <th>{{ $t('report.excludeElements') }}</th>
              <td v-if="reportDatum.useExcludeSelectors">
                <ul>
                  <li v-for="(selector, index) in reportDatum.excludeSelectors.split('\n')"
                      :key="index">{{ selector }}</li>
                </ul>
              </td>
              <td v-else>{{ $t('report.notAvailable') }}</td>
            </tr>
            <tr>
              <th>{{ $t('report.includeElements') }}</th>
              <td v-if="reportDatum.useIncludeSelectors">
                <ul>
                  <li v-for="(selector, index) in reportDatum.includeSelectors.split('\n')"
                      :key="index">{{ selector }}</li>
                </ul>
              </td>
              <td v-else>{{ $t('report.all') }}</td>
            </tr>
          </table>
        </div><div class="section-capture">
          <report-capture v-if="reportDatum.deviceType < 10"
                          :issueData="issueData"
                          :report="report"
                          :nodeDataMap="nodeDataMap"
                          :documentView="true"
                          @imageLoaded="imageLoaded" />
          <report-image v-else
                        :issueData="issueData"
                        :report="report"
                        :dataMap="nodeDataMap"
                        :documentView="true"
                        @imageLoaded="imageLoaded" />
        </div>
        <div class="clearfix"></div>
      </section>

      <section class="break">
        <h2>
          <i class="icon logo--seeall"></i> Result
        </h2>
        <report-summary :report="report"
                        :summaryMap="summaryMap"
                        :documentView="true" />
      </section>

      <template v-for="category in swasCategory">
        <section v-if="categoryResultMap && categoryResultMap[category.id]"
                 class="break"
                 :key="category.id">
          <h2>
            <i class="icon logo--seeall"></i> Checklist {{ category.id % 100 }}. {{ category.titleEn }}
          </h2>
          <report-issue-list :currentCategory="category"
                             :categoryResultMap="categoryResultMap[category.id]"
                             :nodeDataMap="nodeDataMap"
                             :issueData="issueData"
                             :report="report"
                             :documentView="true" />
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import ReportSummary from './ReportSummary'
import ReportCapture from './ReportCapture'
import ReportImage from './ReportImage'
import ReportIssueList from './ReportIssueList'
import reportApi from '@/api/report'
import ruleUiType from '@/data/rule-ui-type'

export default {
  name: 'ReportDocument',
  components: {
    ReportSummary,
    ReportCapture,
    ReportImage,
    ReportIssueList
  },
  data () {
    return {
      rowspanCache: {},
      categoryResultMap: {},
      isDataReady: false,
      isResultReady: false,
      report: {},
      reportId: null,
      issueData: null,
      nodeDataMap: null
    }
  },
  computed: {
    ...mapGetters(['swasCategory']),

    reportDatum () {
      return this.report.ReportDatum
    },
    title () {
      return this.reportDatum.title || 'N/A'
    },
    deviceType () {
      return this.reportDatum.deviceType === 1 ? 'Desktop' : 'Mobile'
    },
    reportUrl () {
      return location.href.replace(/\/view\//i, '/')
    },
    summaryMap () {
      const scoreMap = this.issueData.scoreMap
      const summaryMap = {}

      for (const categoryData of Object.values(this.swasCategory)) {
        const categoryId = categoryData.id
        let resultType
        let resultCssClass
        let passCount = 0
        let errorCount = 0
        let criticalPassCount = 0
        let criticalErrorCount = 0

        if (scoreMap[categoryId]) {
          _.each(scoreMap[categoryId], subScore => {
            passCount += subScore.passCount
            errorCount += subScore.errorCount
            criticalPassCount += subScore.criticalPassCount
            criticalErrorCount += subScore.criticalErrorCount
          })

          if (errorCount) {
            resultType = 'E'
            resultCssClass = 'error'
          } else if (passCount) {
            resultType = 'None'
            resultCssClass = 'none'
            // resultType = 'P'
            // resultCssClass = 'pass'
          }
        } else {
          resultType = 'None'
          resultCssClass = 'none'
        }

        summaryMap[categoryId] = {
          passCount,
          errorCount,
          criticalPassCount,
          criticalErrorCount,
          resultType,
          resultCssClass
        }
      }

      return summaryMap
    }
  },
  created () {
    this.ruleUiType = ruleUiType
    this.initReport()
  },
  methods: {
    imageLoaded () {
      setTimeout(() => {
        window.print()
      }, 500)
    },
    initReport () {
      this.reportId = Number(this.$route.params.id)

      reportApi.findById(this.reportId).then(response => {
        this.report = response.data
        this.issueData = this.report.jsonObject
        this.nodeDataMap = this.report.nodeDataObject
        this.isDataReady = true
        const categoryIds = String(Object.keys(this.issueData.scoreMap).join(','))
        return reportApi.findCategoryResult(this.reportId, categoryIds + ',')
      }).then(response => {
        const categoryIds = Object.keys(this.issueData.scoreMap)

        for (let i = 0; i < categoryIds.length; i++) {
          this.$set(this.categoryResultMap, categoryIds[i], response.data[categoryIds[i]])
        }
        this.isResultReady = true
      }).catch(err => {
        const isHandled = this.handleAjaxError(err)

        if (!isHandled) {
          alert(this.$t('system.wrongApproach'))
          window.location.replace('/')
        }
      })
    },
    rowspan (col, rule, row, sub, cat) {
      if (this.rowspanCache[`${rule}-${row}`]) {
        return this.rowspanCache[`${rule}-${row}`]
      }

      let rowData = this.categoryResultMap[cat][sub][rule][row]
      let hasArray = false
      let arrayField = []
      for (const column of col) {
        if (column.array) {
          hasArray = true
          arrayField.push(column.field)
        }
      }
      if (!hasArray) {
        return null
      }

      let rowspan = 1
      for (const field of arrayField) {
        if (rowData.preview && rowData.preview[field]) {
          rowspan = Math.max(rowspan, rowData.preview[field].length)
        }
      }
      this.rowspanCache[`${rule}-${row}`] = rowspan
      return rowspan
    },
    getCode (id) {
      const nodeData = this.nodeDataMap[id]

      if (!nodeData) {
        return ''
      }

      let isSimpleTag

      switch (nodeData.tagName) {
        case 'HTML':
        case 'BODY':
          isSimpleTag = false
          break
        default:
          isSimpleTag = true
      }

      if (isSimpleTag) {
        const codeEl = document.getElementById(`code-id-${id}`)
        if (codeEl) {
          return codeEl.innerText.trim()
        }
        return 'not available'
      } else {
        let attrString = ''
        let attrValues = []

        if (nodeData.attrs) {
          for (let [key, value] of Object.entries(nodeData.attrs)) {
            attrValues.push(`${key}="${value}"`)
          }
        }

        if (attrValues.length) {
          attrString = ' ' + attrValues.join(' ')
        }
        const tagName = nodeData.tagName.toLowerCase()
        return `<${tagName}${attrString}>...</${tagName}>`
      }
    }
  },
  destroyed () {
  }
}
</script>

<style lang="scss" scoped>
.document-view {
  background: #ffffff;

  .document-view-wrap {
    @media screen {
      padding: 0 20px;
      max-width: 1240px;
      margin: 0 auto;
    }
  }

  section {
    h2 {
      font-size: 25px;
      padding: 5px 0;
      font-weight: bold;

      .icon {
        position: relative;
        top: -1px;
        width: 70px;
        height: 21px;
        vertical-align: middle;
        margin-right: 3px;
      }
    }

    .section-content {
      float: left;
      width: calc(100% - 400px);
      border-top: 3px solid #dddddd;
    }

    .section-capture {
      float: right;
      width: 400px;
      border-top: 3px solid #dddddd;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;

      & /deep/ {
        .capture-container {
          border-radius: 0;

          img {
            display: none;
          }
        }

        .report-image {
          .image-container {
            padding-top: 5px;
            margin-bottom: 15px;
            page-break-inside: avoid;
          }
        }
      }
    }

    .report-info {
      width: calc(100% - 5px);
      margin-bottom: 60px;

      th {
        padding: 10px 0;
        width: 200px;
        vertical-align: top;
        border-bottom: 1px solid #e7e7e7;
        font-weight: normal;
        color: #666666;
      }

      td {
        padding: 10px 0;
        vertical-align: top;
        border-bottom: 1px solid #e7e7e7;
      }
    }

    & /deep/ {
      .issue-summary {
        border-radius: 0;
        box-shadow: none;
        margin-bottom: 50px;

        .checklist-table {
          margin: 10px 0;
          width: 100%;
        }
      }

      .issue-sheet {
        border-radius: 0;
        box-shadow: none;

        .issue-detail {
          margin: 5px 0 20px;

          .sub-categories {
            margin: 0 0 15px;
          }

          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
        }
      }
    }

    &.break {
      page-break-after: always;
    }

    &.header {
      padding: 10px 0;
      h1 {
        font-weight: bold;
        font-size: 30px;
      }

      p {
        font-size: 20px;
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
        margin-bottom: 10px;
        padding: 10px 0;

        .group {
          background: #ec0047;
          border-radius: 6px;
          color: #ffffff;
          font-size: 15px;
          padding: 3px 7px;
          display: inline-block;
          vertical-align: middle;
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
          font-weight: bold;
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/valid-member/report/ReportDocument.vue